import { useEffect, useState } from "react";
import ThemeToggle from "../components/ThemeToggle";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronRight,
  Eye,
  EyeOff,
  Fingerprint,
  KeyRound,
  Lock,
  Mail,
  MonitorSmartphone,
  ScanFace,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
  Gift,
} from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import TwoFactorAuth from "../components/TwoFactorAuth";
import LoginAreaLinks from "../components/LoginAreaLinks";

const LOGO_URL = "/images/logo-full.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Impostare su true per mostrare di nuovo "Continua con Google". */
const ENABLE_GOOGLE_LOGIN = false;

const GoogleIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

const base64UrlToArrayBuffer = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output.buffer;
};

const arrayBufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const normalizeCreationOptions = (options) => ({
  ...options,
  challenge: base64UrlToArrayBuffer(options.challenge),
  user: {
    ...options.user,
    id: base64UrlToArrayBuffer(options.user.id),
  },
  excludeCredentials: options.excludeCredentials?.map((credential) => ({
    ...credential,
    id: base64UrlToArrayBuffer(credential.id),
  })),
});

const normalizeRequestOptions = (options) => ({
  ...options,
  challenge: base64UrlToArrayBuffer(options.challenge),
  allowCredentials: options.allowCredentials?.map((credential) => ({
    ...credential,
    id: base64UrlToArrayBuffer(credential.id),
  })),
});

const credentialToJSON = (credential) => ({
  id: credential.id,
  rawId: arrayBufferToBase64Url(credential.rawId),
  type: credential.type,
  authenticatorAttachment: credential.authenticatorAttachment,
  clientExtensionResults: credential.getClientExtensionResults(),
  response: Object.fromEntries(
    Object.entries({
      attestationObject: credential.response.attestationObject,
      authenticatorData: credential.response.authenticatorData,
      clientDataJSON: credential.response.clientDataJSON,
      signature: credential.response.signature,
      userHandle: credential.response.userHandle,
    })
      .filter(([, value]) => value)
      .map(([key, value]) => [key, arrayBufferToBase64Url(value)])
  ),
});

const QuickAccessButton = ({ icon: Icon, title, description, badge, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md"
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
      <Icon className="h-5 w-5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        {title}
        {badge ? (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-600">
            {badge}
          </span>
        ) : null}
      </span>
      <span className="mt-1 block text-xs text-slate-500">{description}</span>
    </span>
    <ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:text-emerald-500" />
  </button>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorMode, setTwoFactorMode] = useState('login'); // 'login' | 'setup'
  const { login, completeExternalLogin, syncSessionFromStorage } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("trial") === "activated") {
      toast.success("Prova gratuita attivata! Accedi con le credenziali create.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    const oauthCode = params.get("oauth_code");
    const oauthError = params.get("oauth_error");

    if (oauthError) {
      toast.error(oauthError);
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    if (!oauthCode) return;

    const completeGoogleLogin = async () => {
      setOauthLoading(true);
      try {
        const { data } = await axios.post(`${API_URL}/api/auth/oauth/complete`, {
          code: oauthCode,
        });
        completeExternalLogin(data);
        toast.success("Accesso con Google effettuato");
        window.history.replaceState({}, "", window.location.pathname);
        navigate("/");
      } catch (error) {
        console.error("Google OAuth complete error:", error);
        const detail = error.response?.data?.detail || "Accesso con Google non riuscito";
        toast.error(detail);
        window.history.replaceState({}, "", window.location.pathname);
      } finally {
        setOauthLoading(false);
      }
    };

    completeGoogleLogin();
  }, [completeExternalLogin, navigate]);

  const handleGoogleLogin = () => {
    if (!API_URL) {
      toast.error("Backend non configurato");
      return;
    }
    window.location.href = `${API_URL}/api/auth/oauth/google/start`;
  };

  const ensurePasskeySupport = async (method) => {
    if (!window.PublicKeyCredential) {
      toast.error(`${method} non è supportato da questo browser`);
      return false;
    }

    try {
      const isAvailable = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!isAvailable) {
        toast.error(`${method} non è disponibile su questo dispositivo`);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Quick access availability error:", error);
      toast.error("Impossibile verificare l'accesso rapido su questo dispositivo");
      return false;
    }
  };

  const registerPasskey = async () => {
    const token = localStorage.getItem("affidya_token");
    if (!token) return false;

    const supported = await ensurePasskeySupport("Passkey");
    if (!supported) return false;

    try {
      setPasskeyLoading(true);
      const { data: options } = await axios.post(
        `${API_URL}/api/auth/passkeys/register/options`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const credential = await navigator.credentials.create({
        publicKey: normalizeCreationOptions(options),
      });

      if (!credential) {
        toast.error("Registrazione passkey annullata");
        return false;
      }

      await axios.post(
        `${API_URL}/api/auth/passkeys/register/verify`,
        { credential: credentialToJSON(credential) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Passkey abilitata per questo dispositivo");
      return true;
    } catch (error) {
      console.error("Passkey registration error:", error);
      const detail = error.response?.data?.detail || "Registrazione passkey non riuscita";
      toast.error(detail);
      return false;
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleQuickAccess = async (method) => {
    const supported = await ensurePasskeySupport(method);
    if (!supported) return;

    try {
      setPasskeyLoading(true);
      const { data } = await axios.post(`${API_URL}/api/auth/passkeys/login/options`);
      const credential = await navigator.credentials.get({
        publicKey: normalizeRequestOptions(data.options),
      });

      if (!credential) {
        toast.error("Accesso passkey annullato");
        return;
      }

      const response = await axios.post(`${API_URL}/api/auth/passkeys/login/verify`, {
        session_id: data.session_id,
        credential: credentialToJSON(credential),
      });

      completeExternalLogin(response.data);
      toast.success("Accesso rapido effettuato");
      navigate("/");
    } catch (error) {
      console.error("Passkey login error:", error);
      const detail = error.response?.data?.detail || "Nessuna passkey valida trovata per questo dispositivo";
      toast.error(detail);
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);

      if (userData?.is_demo_account) {
        toast.success("Accesso effettuato");
        navigate("/");
        return;
      }

      if (userData?.account_status === "trial_expired") {
        toast.info("La prova gratuita è terminata. Effettua l'upgrade per continuare.");
        navigate("/trial-expired");
        return;
      }

      // Dopo il login con credenziali, il 2FA diventa obbligatorio.
      // Verifichiamo se l'utente ha già il 2FA configurato.
      const token = localStorage.getItem('affidya_token');
      let has2FAEnabled = false;

      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/status`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const status = await response.json();
            has2FAEnabled = !!status.enabled;
          }
        } catch (statusError) {
          console.error("2FA status check error:", statusError);
        }
      }

      if (!has2FAEnabled) {
        // 2FA non configurato -> forza il flusso di setup
        setTwoFactorMode('setup');
        setShowTwoFactor(true);
        return;
      }

      // 2FA già configurato -> forza sempre la verifica 2FA sul login
      setTwoFactorMode('login');
      setShowTwoFactor(true);
      return;
      
    } catch (error) {
      console.error("Login error:", error);
      const detail = error.response?.data?.detail;
      
      // Se l'errore è 401 e potrebbe essere richiesto 2FA
      if (error.response?.status === 401 && detail?.includes('2FA')) {
        setTwoFactorMode('login');
        setShowTwoFactor(true);
        return;
      }
      
      // Handle specific status codes
      if (error.response?.status === 402) {
        toast.error("Pagamento in attesa. Completa il pagamento per accedere.");
      } else if (error.response?.status === 403) {
        toast.error(detail || "Account disattivato o abbonamento scaduto.");
      } else {
        toast.error(detail || "Credenziali non valide");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorComplete = async (userData) => {
    syncSessionFromStorage();
    setShowTwoFactor(false);
    // Se userData è presente, significa che il login con 2FA è stato completato.
    // Se è null/undefined, il setup 2FA è andato a buon fine.
    toast.success("Accesso effettuato con successo!");
    const promptDismissed = localStorage.getItem("affidya_passkey_prompt_dismissed") === "true";
    if (!promptDismissed && window.PublicKeyCredential) {
      const shouldRegister = window.confirm("Vuoi abilitare impronta, Face ID o passkey per i prossimi accessi?");
      if (shouldRegister) {
        await registerPasskey();
      } else {
        localStorage.setItem("affidya_passkey_prompt_dismissed", "true");
      }
    }
    const meToken = localStorage.getItem("affidya_token");
    if (meToken) {
      try {
        const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${meToken}` },
        });
        if (meRes.ok) {
          const meData = await meRes.json();
          if (meData.account_status === "trial_expired") {
            navigate("/trial-expired");
            return;
          }
        }
      } catch (_) {
        /* ignore */
      }
    }
    navigate("/");
  };

  const handleBackToLogin = () => {
    setShowTwoFactor(false);
    setTwoFactorMode('login');
  };

  return (
    <>
      {showTwoFactor ? (
        <TwoFactorAuth
          onVerificationComplete={handleTwoFactorComplete}
          onBack={handleBackToLogin}
          email={email}
          isSetup={twoFactorMode === 'setup'}
        />
      ) : (
        <div className="min-h-screen overflow-hidden bg-[#f8fbfa] text-slate-900">
          <ThemeToggle className="fixed right-3 top-3 z-50" testId="theme-toggle-login" />
          <div className="grid min-h-screen lg:grid-cols-[0.86fr_1.14fr]">
            <aside className="relative hidden overflow-hidden bg-gradient-to-br from-[#f3faf8] via-white to-[#eef8f4] px-10 py-10 lg:flex lg:flex-col">
              <div className="relative z-10 max-w-md">
                <img src={LOGO_URL} alt="Affidya" className="mb-9 h-14 w-auto" />
                <h1 className="text-[2rem] font-semibold leading-tight tracking-tight text-slate-900">
                  Sistema digitale per l'assistenza
                </h1>
                <p className="mt-4 text-sm leading-6 text-slate-600">
                  La piattaforma completa per gestire servizi, operatori e assistiti in modo semplice, sicuro e veloce.
                </p>

                <div className="mt-9 space-y-5">
                  {[
                    {
                      icon: ShieldCheck,
                      title: "Sicuro e conforme",
                      text: "I tuoi dati sono protetti secondo i più elevati standard.",
                    },
                    {
                      icon: Sparkles,
                      title: "Accesso rapido",
                      text: "Entra in piattaforma in pochi secondi, senza complicazioni.",
                    },
                    {
                      icon: MonitorSmartphone,
                      title: "Sempre con te",
                      text: "Accedi da qualsiasi dispositivo, quando e dove vuoi.",
                    },
                    {
                      icon: Users,
                      title: "Assistenza dedicata",
                      text: "Un team sempre a disposizione per supportarti.",
                    },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100/80 text-emerald-700">
                        <item.icon className="h-5 w-5" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{item.title}</span>
                        <span className="mt-0.5 block text-xs leading-5 text-slate-500">{item.text}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute -bottom-24 -left-28 h-72 w-[32rem] rounded-[55%] bg-emerald-200/55 blur-2xl" />
              <div className="absolute -bottom-16 left-0 h-48 w-[115%] rounded-[55%_45%_0_0] bg-emerald-100/80" />
              <div className="absolute bottom-0 left-[-10%] h-40 w-[78%] rounded-tr-[100%] bg-emerald-200/65" />
              <div className="absolute bottom-[-2rem] right-[-18%] h-52 w-[78%] rounded-tl-[100%] bg-[#d7f1e8]/85" />
              <div className="absolute bottom-12 left-16 h-28 w-44 opacity-30 [background-image:radial-gradient(#55b990_1px,transparent_1px)] [background-size:12px_12px]" />
            </aside>

            <main className="flex min-h-screen items-center justify-center px-4 py-4 sm:px-6 lg:px-8">
              <div className="w-full max-w-[520px]">
                <div className="mb-4 text-center lg:hidden">
                  <img src={LOGO_URL} alt="Affidya" className="mx-auto mb-3 h-14" />
                  <p className="text-slate-500">Sistema digitale per l'assistenza</p>
                </div>

                <section className="rounded-[1.6rem] border border-white/80 bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.10)] sm:p-6">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900">Accedi al tuo account</h2>
                    <p className="mt-1 text-sm text-slate-500">Scegli il metodo più veloce per te</p>
                  </div>

                  <div className="mt-5">
                    <p className="mb-2 text-sm font-semibold text-emerald-600">Accesso rapido (consigliato)</p>
                    <div className="space-y-2">
                      <QuickAccessButton
                        icon={Fingerprint}
                        title="Accedi con impronta"
                        description={passkeyLoading ? "Verifica in corso..." : "Usa il tuo dispositivo"}
                        onClick={() => handleQuickAccess("Accesso con impronta")}
                      />
                      <QuickAccessButton
                        icon={ScanFace}
                        title="Accedi con Face ID"
                        description={passkeyLoading ? "Verifica in corso..." : "Riconoscimento facciale"}
                        onClick={() => handleQuickAccess("Accesso con Face ID")}
                      />
                      <QuickAccessButton
                        icon={KeyRound}
                        title="Passkey"
                        description={passkeyLoading ? "Verifica in corso..." : "Accesso sicuro senza password"}
                        badge="Novità"
                        onClick={() => handleQuickAccess("Passkey")}
                      />
                    </div>
                  </div>

                  {ENABLE_GOOGLE_LOGIN ? (
                    <>
                      <div className="my-4 flex items-center gap-4">
                        <span className="h-px flex-1 bg-slate-200" />
                        <span className="text-xs font-medium text-slate-400">Altri metodi di accesso</span>
                        <span className="h-px flex-1 bg-slate-200" />
                      </div>

                      <button
                        type="button"
                        onClick={handleGoogleLogin}
                        disabled={oauthLoading}
                        className="group flex h-12 w-full items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {oauthLoading ? (
                          <span className="h-6 w-6 rounded-full border-2 border-slate-200 border-t-emerald-500 animate-spin" />
                        ) : (
                          <GoogleIcon />
                        )}
                        <span className="flex-1 text-sm font-semibold text-slate-800">
                          {oauthLoading ? "Completamento accesso Google..." : "Continua con Google"}
                        </span>
                        <ChevronRight className="h-5 w-5 text-slate-300 transition group-hover:text-emerald-500" />
                      </button>
                    </>
                  ) : null}

                  <div className="my-4 flex items-center gap-4">
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className="text-xs font-medium text-slate-400">oppure email e password</span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                        Email
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="nome@struttura.it"
                          className="h-11 rounded-xl pl-11 text-sm"
                          required
                          data-testid="login-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                          Password
                        </Label>
                        <Link
                          to="/forgot-password"
                          className="text-xs font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                        >
                          Password dimenticata?
                        </Link>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          className="h-11 rounded-xl pl-11 pr-11 text-sm"
                          required
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                          data-testid="toggle-password"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="h-11 w-full rounded-xl bg-emerald-600 text-sm font-semibold hover:bg-emerald-700"
                      data-testid="login-submit"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                          Accesso in corso...
                        </span>
                      ) : (
                        "Accedi con email"
                      )}
                    </Button>
                  </form>

                  <div className="mt-4 border-t border-slate-100 pt-4 text-center">
                    <p className="mb-2 text-xs text-slate-500">Non hai ancora un account?</p>
                    <Link to="/register" className="block">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 w-full rounded-xl border-slate-200 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                        data-testid="register-btn"
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Registrati o richiedi accesso
                      </Button>
                    </Link>
                  </div>

                  <Link
                    to="/register/trial"
                    className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50/70 p-4 text-left transition hover:border-emerald-200 hover:bg-emerald-50"
                    data-testid="trial-cta"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                      <Gift className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900">
                        Prova gratuita di 14 giorni
                      </span>
                      <span className="mt-0.5 block text-xs leading-5 text-slate-600">
                        Attiva la demo e scopri Affidya senza impegno.
                      </span>
                      <span className="mt-1.5 block text-[11px] font-medium text-emerald-700">
                        14 giorni gratuiti — nessuna carta richiesta
                      </span>
                    </span>
                    <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-emerald-400" />
                  </Link>
                </section>

                <LoginAreaLinks current="main" theme="emerald" className="mt-5" />

                <footer className="mt-5 text-center">
                  <div className="flex justify-center gap-5 text-xs text-slate-500">
                    <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
                    <Link to="/cookies" className="hover:text-slate-700">Cookie Policy</Link>
                    <Link to="/terms" className="hover:text-slate-700">Termini di Servizio</Link>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    © {new Date().getFullYear()} Affidya S.r.l. - Tutti i diritti riservati
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Powered by <a href="https://rco-group.it" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">rco-group.it</a>
                    <span className="mx-2">•</span>
                    <span className="text-slate-300">Ver. 2</span>
                  </p>
                </footer>
              </div>
            </main>
          </div>
        </div>
      )}
    </>
  );
}
